<template>
  <div class="features-page">
    <!-- Hero Section -->
    <section class="hero">
      <div class="hero-inner">
        <h1>AI智能客服系统</h1>
        <p>7×24小时智能服务，提升客户满意度，降低运营成本</p>
        <div class="hero-ctas">
          <button class="btn primary" @click="tryFree">立即体验</button>
          <button class="btn secondary" @click="contactSales">了解更多</button>
        </div>
      </div>
    </section>

    <!-- 数据展示区 -->
    <section class="stats">
      <div class="stats-grid">
        <div class="stat-item" v-for="stat in stats" :key="stat.id">
          <div class="stat-number">
            {{ stat.number }}<small>{{ stat.unit }}</small>
          </div>
          <div class="stat-label">{{ stat.label }}</div>
        </div>
      </div>
    </section>

    <!-- 核心功能 -->
    <section class="features">
      <div class="section-header">
        <h2 class="section-title">核心功能</h2>
        <p class="section-subtitle">强大的AI能力，满足多种业务场景需求</p>
      </div>
      <div class="features-grid">
        <div class="feature-card" v-for="(feature, idx) in features" :key="feature.id">
          <div class="feature-number">{{ idx + 1 }}</div>
          <h3 class="feature-title">{{ feature.title }}</h3>
          <p class="feature-desc">{{ feature.desc }}</p>
          <div class="feature-highlight" v-if="feature.highlight">{{ feature.highlight }}</div>
        </div>
      </div>
    </section>

    <!-- 应用场景 -->
    <section class="scenarios" ref="scenariosSection">
      <div class="section-header">
        <h2 class="section-title">应用场景</h2>
        <p class="section-subtitle">覆盖多个行业，灵活适配各类业务需求</p>
      </div>
      <div class="scenarios-grid">
        <div class="scenario-card" v-for="scenario in scenarios" :key="scenario.id">
          <!-- <div class="scenario-icon">{{ scenario.icon }}</div> -->
          <h3 class="scenario-title">{{ scenario.title }}</h3>
          <p class="scenario-desc">{{ scenario.desc }}</p>
          <ul class="scenario-points">
            <li v-for="(point, i) in scenario.points" :key="i">{{ point }}</li>
          </ul>
        </div>
      </div>
    </section>

    <!-- 技术优势 -->
    <section class="advantages">
      <div class="section-header">
        <h2 class="section-title">技术优势</h2>
        <p class="section-subtitle">先进的技术架构，保障系统稳定高效</p>
      </div>
      <div class="advantages-grid">
        <div class="advantage-card" v-for="advantage in advantages" :key="advantage.id">
          <h3 class="advantage-title">{{ advantage.title }}</h3>
          <p class="advantage-desc">{{ advantage.desc }}</p>
        </div>
      </div>
    </section>

    <!-- CTA -->
    <section class="cta-bar">
      <div class="cta-inner">
        <div class="cta-text">准备好了吗？立即体验智能客服系统</div>
        <div>
          <button class="btn primary" @click="tryFree">立即体验</button>
        </div>
      </div>
    </section>

    <!-- 邮件弹窗 -->
    <transition name="slide-down">
      <div class="email-modal" v-if="showEmailModal" @click="closeModal">
        <div class="email-modal-content" @click.stop>
          <div class="email-modal-header">
            <h3>联系客服</h3>
            <button class="close-btn" @click="closeModal">×</button>
          </div>
          <div class="email-modal-body">
            <p class="email-label">客服邮箱</p>
            <div class="email-box" @click="copyEmail">
              <span class="email-text">{{ customerEmail }}</span>
              <span class="copy-icon">📋</span>
            </div>
            <p class="copy-hint" v-if="showCopySuccess">✓ 邮箱已复制</p>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: "Features",
  data() {
    return {
      showEmailModal: false,
      customerEmail: 'tech@yihui.com',
      showCopySuccess: false,
      stats: [
        {
          id: 1,
          number: "3",
          unit: "秒",
          label: "平均响应时间"
        },
        {
          id: 2,
          number: "24",
          unit: "小时",
          label: "全天候服务"
        },
        {
          id: 3,
          number: "90",
          unit: "%",
          label: "问题解决率"
        }
      ],
      features: [
        {
          id: 1,
          title: "智能对话",
          desc: "基于深度学习的自然语言处理技术，理解用户意图，提供精准回答",
          highlight: "支持多轮对话"
        },
        {
          id: 2,
          title: "知识库管理",
          desc: "灵活的知识库配置，支持文档、FAQ、API等多种知识源",
          highlight: "智能学习优化"
        },
        {
          id: 3,
          title: "多渠道接入",
          desc: "支持网页、APP、微信、企业微信等多种渠道统一接入",
          highlight: "一站式管理"
        },
        {
          id: 4,
          title: "数据分析",
          desc: "实时监控对话数据，提供多维度统计分析报表",
          highlight: "洞察用户需求"
        },
        {
          id: 5,
          title: "人机协作",
          desc: "智能转接人工客服，实现AI与人工的无缝协作",
          highlight: "提升服务效率"
        },
        {
          id: 6,
          title: "个性化定制",
          desc: "支持对话流程、UI界面、业务逻辑的灵活定制",
          highlight: "适配业务场景"
        }
      ],
      scenarios: [
        {
          id: 1,
          icon: "🏢",
          title: "企业客服",
          desc: "为企业提供7×24小时智能客服支持",
          points: [
            "售前咨询解答",
            "售后问题处理",
            "产品信息查询",
            "订单状态跟踪"
          ]
        },
        {
          id: 2,
          icon: "🛍️",
          title: "电商零售",
          desc: "提升电商平台的客户服务体验",
          points: [
            "商品推荐",
            "促销活动咨询",
            "物流查询",
            "退换货处理"
          ]
        },
        {
          id: 3,
          icon: "🏥",
          title: "医疗健康",
          desc: "智能医疗咨询和预约服务",
          points: [
            "症状初筛",
            "就医指导",
            "预约挂号",
            "报告解读"
          ]
        },
        {
          id: 4,
          icon: "🏦",
          title: "金融服务",
          desc: "安全可靠的金融业务咨询",
          points: [
            "业务办理指引",
            "账户查询",
            "风险提醒",
            "投资建议"
          ]
        }
      ],
      advantages: [
        {
          id: 1,
          title: "高可用架构",
          desc: "采用分布式架构设计，保证系统99.9%以上的可用性，支持高并发访问"
        },
        {
          id: 2,
          title: "安全保障",
          desc: "数据加密传输，权限精细管理，通过多项安全认证，保护用户隐私"
        },
        {
          id: 3,
          title: "快速部署",
          desc: "支持云端部署和私有化部署，可在1天内完成系统上线"
        },
        {
          id: 4,
          title: "持续优化",
          desc: "AI模型持续学习优化，定期更新功能，保持技术领先"
        }
      ]
    };
  },
  methods: {
    contactSales() {
      // 滚动到应用场景模块
      if (this.$refs.scenariosSection) {
        this.$refs.scenariosSection.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }
    },
    tryFree() {
      // 显示邮件弹窗
      this.showEmailModal = true;
    },
    closeModal() {
      this.showEmailModal = false;
      this.showCopySuccess = false;
    },
    copyEmail() {
      // 复制邮箱到剪贴板
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(this.customerEmail).then(() => {
          this.showCopySuccess = true;
          setTimeout(() => {
            this.showCopySuccess = false;
          }, 2000);
        }).catch(err => {
          console.error('复制失败:', err);
          this.fallbackCopyEmail();
        });
      } else {
        this.fallbackCopyEmail();
      }
    },
    fallbackCopyEmail() {
      // 降级方案：使用传统方法复制
      const textArea = document.createElement('textarea');
      textArea.value = this.customerEmail;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        this.showCopySuccess = true;
        setTimeout(() => {
          this.showCopySuccess = false;
        }, 2000);
      } catch (err) {
        console.error('复制失败:', err);
        alert('复制失败，请手动复制：' + this.customerEmail);
      }
      document.body.removeChild(textArea);
    }
  }
};
</script>

<style scoped>
.features-page {
  --primary-blue: #0066ff;
  --primary-blue-light: #4d94ff;
  --primary-blue-dark: #0052cc;
  --bg-light-blue: #ebf3ff;
  --bg-soft-blue: #f0f7ff;
  --text-dark: #1a1a1a;
  --text-gray: #666666;
  --white: #ffffff;
  --card-shadow: 0 4px 20px rgba(0, 102, 255, 0.08);
  --card-hover-shadow: 0 8px 30px rgba(0, 102, 255, 0.15);
  
  font-family: -apple-system, BlinkMacSystemFont, "PingFang SC", "Helvetica Neue",
    "Microsoft YaHei", sans-serif;
  color: var(--text-dark);
  line-height: 1.6;
  background: var(--white);
}

/* Hero Section */
.hero {
  background: linear-gradient(180deg, #e0f3ff 0%, rgba(69, 161, 255, 0.1) 50%, var(--white) 100%);
  color: var(--text-dark);
  padding: 80px 16px 60px;
  text-align: center;
  position: relative;
  overflow: hidden;
}

.hero::before {
  content: "";
  position: absolute;
  top: -50%;
  right: -20%;
  width: 600px;
  height: 600px;
  background: radial-gradient(circle, rgba(0, 102, 255, 0.08) 0%, transparent 70%);
  border-radius: 50%;
}

.hero-inner {
  max-width: 1100px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
}

.hero h1 {
  font-size: 42px;
  font-weight: 700;
  margin: 0 0 16px;
  line-height: 1.2;
}

.hero p {
  margin: 0 0 32px;
  font-size: 18px;
  color: var(--text-gray);
  line-height: 1.8;
}

.hero-ctas {
  display: flex;
  gap: 16px;
  justify-content: center;
}

.btn {
  padding: 12px 32px;
  border-radius: 24px;
  border: none;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn.primary {
  background: var(--primary-blue);
  color: #fff;
  box-shadow: 0 4px 15px rgba(0, 102, 255, 0.3);
}

.btn.primary:hover {
  background: var(--primary-blue-dark);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 102, 255, 0.4);
}

.btn.secondary {
  background: white;
  color: var(--text-dark);
  border: 1px solid #e5e5e5;
}

.btn.secondary:hover {
  border-color: var(--primary-blue);
  color: var(--primary-blue);
}

/* 数据展示区 */
.stats {
  padding: 60px 16px;
  background: var(--white);
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px;
  max-width: 1100px;
  margin: 0 auto;
  text-align: center;
}

.stat-item {
  padding: 30px 20px;
}

.stat-number {
  font-size: 48px;
  font-weight: 700;
  color: var(--primary-blue);
  margin-bottom: 12px;
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 4px;
}

.stat-number small {
  font-size: 24px;
  font-weight: 600;
}

.stat-label {
  font-size: 16px;
  color: var(--text-gray);
}

/* 核心功能 */
.features {
  padding: 60px 16px;
  background: linear-gradient(180deg, var(--white) 0%, var(--bg-soft-blue) 100%);
}

.section-header {
  text-align: center;
  margin-bottom: 50px;
}

.section-title {
  font-size: 36px;
  font-weight: 700;
  color: var(--text-dark);
  margin-bottom: 12px;
}

.section-subtitle {
  color: var(--text-gray);
  font-size: 16px;
  max-width: 600px;
  margin: 0 auto;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  max-width: 1100px;
  margin: 0 auto;
}

.feature-card {
  background: var(--white);
  padding: 32px 24px;
  border-radius: 16px;
  border: 1px solid rgba(0, 102, 255, 0.08);
  transition: all 0.3s ease;
  position: relative;
}

.feature-card:hover {
  transform: translateY(-8px);
  box-shadow: var(--card-hover-shadow);
  border-color: transparent;
}

.feature-number {
  position: absolute;
  top: 20px;
  right: 24px;
  font-size: 64px;
  font-weight: 800;
  color: var(--bg-soft-blue);
  line-height: 1;
}

.feature-title {
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 12px;
  color: var(--text-dark);
  position: relative;
}

.feature-desc {
  color: var(--text-gray);
  line-height: 1.8;
  font-size: 14px;
  position: relative;
  margin-bottom: 12px;
}

.feature-highlight {
  font-size: 13px;
  color: var(--primary-blue);
  font-weight: 500;
}

/* 应用场景 */
.scenarios {
  padding: 60px 16px;
  background: var(--bg-light-blue);
}

.scenarios-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  max-width: 1100px;
  margin: 0 auto;
}

.scenario-card {
  background: var(--white);
  padding: 32px 28px;
  border-radius: 16px;
  border: 1px solid rgba(0, 102, 255, 0.08);
  transition: all 0.3s ease;
}

.scenario-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--card-shadow);
  border-color: transparent;
}

.scenario-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.scenario-title {
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 12px;
  color: var(--text-dark);
}

.scenario-desc {
  color: var(--text-gray);
  font-size: 14px;
  line-height: 1.7;
  margin-bottom: 16px;
}

.scenario-points {
  list-style: none;
  padding: 0;
  margin: 0;
}

.scenario-points li {
  padding: 6px 0;
  font-size: 14px;
  color: var(--text-gray);
  position: relative;
  padding-left: 20px;
}

.scenario-points li::before {
  content: "✓";
  position: absolute;
  left: 0;
  color: var(--primary-blue);
  font-weight: bold;
}

/* 技术优势 */
.advantages {
  padding: 60px 16px;
  background: var(--white);
}

.advantages-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  max-width: 1100px;
  margin: 0 auto;
}

.advantage-card {
  background: var(--bg-soft-blue);
  padding: 32px 28px;
  border-radius: 16px;
  transition: all 0.3s ease;
}

.advantage-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--card-shadow);
}

.advantage-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 12px;
  color: var(--text-dark);
}

.advantage-desc {
  color: var(--text-gray);
  font-size: 14px;
  line-height: 1.8;
}

/* CTA */
.cta-bar {
  padding: 40px 16px;
  background: linear-gradient(135deg, var(--primary-blue) 0%, var(--primary-blue-light) 100%);
}

.cta-inner {
  max-width: 1100px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
}

.cta-text {
  font-size: 20px;
  font-weight: 600;
  color: var(--white);
}

.cta-bar .btn.primary {
  background: var(--white);
  color: var(--primary-blue);
}

.cta-bar .btn.primary:hover {
  background: var(--bg-light-blue);
  color: var(--primary-blue-dark);
}

/* 邮件弹窗 */
.email-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  padding-top: 60px;
  z-index: 1000;
  overflow-y: auto;
}

.email-modal-content {
  background: var(--white);
  border-radius: 16px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.15);
  max-width: 500px;
  width: calc(100% - 32px);
  margin: 0 16px;
  animation: slideDown 0.3s ease;
  height: fit-content;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.slide-down-enter-active, .slide-down-leave-active {
  transition: opacity 0.3s ease;
}

.slide-down-enter-from, .slide-down-leave-to {
  opacity: 0;
}

.email-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 28px;
  border-bottom: 1px solid #f0f0f0;
}

.email-modal-header h3 {
  font-size: 20px;
  font-weight: 600;
  color: var(--text-dark);
  margin: 0;
}

.close-btn {
  background: none;
  border: none;
  font-size: 32px;
  color: var(--text-gray);
  cursor: pointer;
  line-height: 1;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.close-btn:hover {
  background: var(--bg-soft-blue);
  color: var(--text-dark);
}

.email-modal-body {
  padding: 32px 28px;
}

.email-label {
  font-size: 14px;
  color: var(--text-gray);
  margin: 0 0 12px;
}

.email-box {
  background: var(--bg-soft-blue);
  border: 2px solid var(--primary-blue);
  border-radius: 12px;
  padding: 16px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.email-box:hover {
  background: var(--bg-light-blue);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 102, 255, 0.15);
}

.email-box:active {
  transform: translateY(0);
}

.email-text {
  font-size: 16px;
  font-weight: 500;
  color: var(--primary-blue);
  user-select: all;
}

.copy-icon {
  font-size: 20px;
  opacity: 0.7;
}

.copy-hint {
  margin: 12px 0 0;
  font-size: 14px;
  color: #52c41a;
  text-align: center;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 响应式 */
@media (max-width: 768px) {
  .hero h1 {
    font-size: 32px;
  }

  .hero p {
    font-size: 16px;
  }

  .hero-ctas {
    flex-direction: column;
  }

  .stats-grid {
    grid-template-columns: 1fr;
    gap: 24px;
  }

  .features-grid {
    grid-template-columns: 1fr;
  }

  .scenarios-grid {
    grid-template-columns: 1fr;
  }

  .advantages-grid {
    grid-template-columns: 1fr;
  }

  .cta-inner {
    flex-direction: column;
    text-align: center;
  }

  .section-title {
    font-size: 28px;
  }

  .stat-number {
    font-size: 40px;
  }

  .stat-number small {
    font-size: 20px;
  }
}
</style>
